import { EventPhoto, PhotoEvent } from 'common-models';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams } from 'react-router-dom';
import { useFileUpload } from 'src/context/FileUploadContext';
import { v4 as uuidv4 } from 'uuid';
import { useDeleteSingleEventMutation, useGetSingleEventWithPhotosQuery } from '../../../features/events/eventsApiSlice';

export const useEventEditorView = () => {
  const auth = useAuth();
  const nav = useNavigate();
  const { addUpload } = useFileUpload();
  const params = useParams();
  const eventId = params.eventId as string;

  // API hooks
  const [deleteEvent] = useDeleteSingleEventMutation();
  const { data: photoEventFromApi = undefined } = useGetSingleEventWithPhotosQuery({ eventId: eventId });

  // State
  const [photoEvent, setPhotoEvent] = useState(undefined as unknown as PhotoEvent);
  const [photoEventPhotos, setEventPhotos] = useState([] as Array<EventPhoto>);
  const [startDate, setStartDate] = useState(undefined as unknown as Date);
  const [endDate, setEndDate] = useState(undefined as unknown as Date);
  const [overlayKey, setOverlayKey] = useState<string>('');

  // Computed values
  const isNewEvent = eventId.toLowerCase() === 'new';

  // Event handlers
  const onDeleteEventClick = async () => {
    if (window.confirm(`Do you really want to delete the event "${photoEvent.title}"? This action cannot be undone.`)) {
      await deleteEvent([photoEvent.id, auth.user?.access_token ?? '']);
      nav({ pathname: '/admin/events' });
    }
  };

  const onFilesChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Files are handled in onSubmitFileUpload
  };

  const onSubmitFileUpload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target: HTMLFormElement = event.target as HTMLFormElement;
    const files = target.querySelector('#files') as HTMLInputElement;

    if (files !== null && files.files !== null) {
      for (let index = 0; index < files.files.length; index++) {
        const file = files.files[index];
        // Don't await this async call to allow parallel uploads
        addUpload(photoEvent.id, file, overlayKey || undefined);
      }
      // Reset the file input
      files.value = '';
    }
  };

  // Effects
  useEffect(() => {
    document.title = `Event Administration - ${photoEvent?.title || 'Loading...'}`;
    window.scrollTo(0, 0);
  }, [photoEvent?.title]);

  useEffect(() => {
    if (isNewEvent) {
      console.info('Setting up new event');
      const now = new Date();
      setPhotoEvent({
        id: uuidv4(),
        title: '',
        isExternalCopyright: false,
        eventStartTime: now,
        eventEndTime: now,
        displayFreeOfChargeText: true,
        isPublic: true,
        isFreeOfCharge: false,
      });
    }
  }, [isNewEvent]);

  useEffect(() => {
    if (photoEvent !== undefined) {
      setPhotoEvent(prev => ({ ...prev, eventStartTime: startDate }));
    }
  }, [photoEvent, startDate]);

  useEffect(() => {
    if (photoEvent !== undefined) {
      setPhotoEvent(prev => ({ ...prev, eventEndTime: endDate }));
    }
  }, [endDate, photoEvent]);

  useEffect(() => {
    if (photoEvent !== undefined) {
      setStartDate(photoEvent.eventStartTime);
      setEndDate(photoEvent.eventEndTime);
    }
  }, [photoEvent]);

  useEffect(() => {
    if (photoEventFromApi !== undefined && photoEventFromApi !== null) {
      const tempEvent: PhotoEvent = {
        id: photoEventFromApi.event.id,
        eventEndTime: photoEventFromApi.event.eventEndTime,
        eventStartTime: photoEventFromApi.event.eventStartTime,
        isExternalCopyright: photoEventFromApi.event.isExternalCopyright,
        isFreeOfCharge: photoEventFromApi.event.isFreeOfCharge,
        displayFreeOfChargeText: photoEventFromApi.event.displayFreeOfChargeText || true,
        isPublic: photoEventFromApi.event.isPublic,
        title: photoEventFromApi.event.title,
        parentEventId: photoEventFromApi.event.parentEventId,
        priceListId: photoEventFromApi.event.priceListId,
        frontImage: photoEventFromApi.event.frontImage,
      };
      setPhotoEvent(tempEvent);
      setEventPhotos(photoEventFromApi.photos);
    }
  }, [photoEventFromApi]);

  return {
    // State
    photoEvent,
    photoEventPhotos,
    eventId,
    isNewEvent,
    overlayKey,

    // Handlers
    onDeleteEventClick,
    onFilesChanged,
    onSubmitFileUpload,
    onOverlayKeyChanged: setOverlayKey,
  };
};
